import React from 'react';
import { Alert, Button, Typography } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import { useNotificationContext } from '../contexts/NotificationProvider';
import { FCM_WEB_PUSH_ENABLED } from '../config/fcmWebPushEnabled';
import { FCM_BLOCK_REASON, getFcmBlockReasonMessage } from '../services/fcmWebRegistration';

/**
 * Shown on the notifications list when browser push is not connected on this device.
 */
const NotificationAlertsSetupPrompt = () => {
  const { isConnected, permission, alertsSetup } = useNotificationContext() ?? {};

  if (!FCM_WEB_PUSH_ENABLED || !alertsSetup?.active || isConnected) {
    return null;
  }

  const { startSetup, isBusy, states } = alertsSetup;
  const { showDenied, showRegistrationRetry, needsPushSetup, blockReason, blockReasonMessage } =
    states;

  let severity = 'info';
  let title = 'Push notifications not connected';
  let body =
    'You can still read notifications here, but you will not get alerts when this tab is in the background until push is enabled on this device.';

  if (showDenied) {
    severity = 'warning';
    title = 'Push notifications blocked';
    body = getFcmBlockReasonMessage(FCM_BLOCK_REASON.PERMISSION_DENIED);
  } else if (showRegistrationRetry) {
    severity = 'warning';
    title = 'Could not connect push';
    body =
      blockReasonMessage ||
      getFcmBlockReasonMessage(blockReason ?? FCM_BLOCK_REASON.PUSH_SERVICE_UNAVAILABLE);
  } else if (needsPushSetup) {
    if (permission === 'granted') {
      title = 'Finish notification setup';
      body =
        'Notifications are allowed in your browser, but this device is not connected yet. Enable push to get background alerts.';
    }
  } else if (blockReasonMessage) {
    severity = 'warning';
    body = blockReasonMessage;
  } else if (blockReason) {
    severity = 'warning';
    body = getFcmBlockReasonMessage(blockReason);
  }

  const actionLabel = showRegistrationRetry ? 'Retry' : showDenied ? 'Show help' : 'Enable';

  return (
    <Alert
      severity={severity}
      icon={
        severity === 'warning' ? (
          <NotificationsOffOutlinedIcon fontSize="inherit" />
        ) : (
          <NotificationsActiveIcon fontSize="inherit" />
        )
      }
      sx={{ mb: 2, alignItems: 'flex-start' }}
      action={
        <Button
          color="inherit"
          size="small"
          variant="outlined"
          onClick={startSetup}
          disabled={isBusy}
          sx={{ whiteSpace: 'nowrap', fontWeight: 600, flexShrink: 0 }}
        >
          {isBusy ? 'Connecting…' : actionLabel}
        </Button>
      }
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35, mb: 0.25 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ lineHeight: 1.45, opacity: 0.92 }}>
        {body}
      </Typography>
    </Alert>
  );
};

export default NotificationAlertsSetupPrompt;
